import axios from 'axios';

const API_URL = '/api';

const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const login = (labCode, username, password) => 
  api.post('/auth/login', { labCode, username, password });

export const register = (userData) => 
  api.post('/auth/register', userData);

export const getCurrentUser = () => 
  api.get('/auth/me');

export const getPublicLaboratories = () =>
  api.get('/auth/laboratories');

// Publications
export const getPublications = () => 
  api.get('/publications');

export const getPublication = (id) => 
  api.get(`/publications/${id}`);

export const createPublication = (data) => 
  api.post('/publications', data);

export const updatePublication = (id, data) => 
  api.put(`/publications/${id}`, data);

export const deletePublication = (id) => 
  api.delete(`/publications/${id}`);

// Scholarships
export const getScholarships = () => 
  api.get('/scholarships');

export const getScholarship = (id) => 
  api.get(`/scholarships/${id}`);

export const createScholarship = (data) => 
  api.post('/scholarships', data);

export const updateScholarship = (id, data) => 
  api.put(`/scholarships/${id}`, data);

export const deleteScholarship = (id) => 
  api.delete(`/scholarships/${id}`);

// Congresses
export const getCongresses = () => 
  api.get('/congresses');

export const getCongress = (id) => 
  api.get(`/congresses/${id}`);

export const createCongress = (data) => 
  api.post('/congresses', data);

export const updateCongress = (id, data) => 
  api.put(`/congresses/${id}`, data);

export const deleteCongress = (id) => 
  api.delete(`/congresses/${id}`);

// Meetings
export const getMeetings = () => 
  api.get('/meetings');

export const getMeeting = (id) => 
  api.get(`/meetings/${id}`);

export const createMeeting = (data) => 
  api.post('/meetings', data);

export const updateMeeting = (id, data) => 
  api.put(`/meetings/${id}`, data);

export const deleteMeeting = (id) => 
  api.delete(`/meetings/${id}`);

export const updateTaskStatus = (meetingId, taskId, status) =>
  api.patch(`/meetings/${meetingId}/tasks/${taskId}/status`, { status });

export const updateTask = (meetingId, taskId, taskData) =>
  api.patch(`/meetings/${meetingId}/tasks/${taskId}`, taskData);

export const deleteTask = (meetingId, taskId) =>
  api.delete(`/meetings/${meetingId}/tasks/${taskId}`);

export const markTaskViewed = (meetingId, taskId) =>
  api.patch(`/meetings/${meetingId}/tasks/${taskId}/view`);

// Task Members
export const addTaskMember = (meetingId, taskId, userId) =>
  api.post(`/meetings/${meetingId}/tasks/${taskId}/members`, { userId });

export const removeTaskMember = (meetingId, taskId, userId) =>
  api.delete(`/meetings/${meetingId}/tasks/${taskId}/members/${userId}`);

// Deleted tasks
export const getDeletedTasks = () =>
  api.get('/meetings/deleted-tasks');

export const getDeletedTask = (id) =>
  api.get(`/meetings/deleted-tasks/${id}`);

export const markDeletedTaskViewed = (id) =>
  api.patch(`/meetings/deleted-tasks/${id}/view`);

// Task Messages
export const getTaskMessages = (meetingId, taskId) =>
  api.get(`/meetings/${meetingId}/tasks/${taskId}/messages`);

export const postTaskMessage = (meetingId, taskId, message, recipients = ['everyone']) =>
  api.post(`/meetings/${meetingId}/tasks/${taskId}/messages`, { message, recipients });

export const getAllMessages = () =>
  api.get('/meetings/messages/all');

export const updateMessage = (messageId, message) =>
  api.put(`/meetings/messages/${messageId}`, { message });

export const deleteMessage = (messageId) =>
  api.delete(`/meetings/messages/${messageId}`);

// Admin routes (Superadmin only)
export const getUsers = () =>
  api.get('/admin/users');

export const createUser = (userData) =>
  api.post('/admin/users', userData);

export const resetUserPassword = (userId, newPassword) =>
  api.post(`/admin/users/${userId}/reset-password`, { newPassword });

export const updateUser = (userId, userData) =>
  api.put(`/admin/users/${userId}`, userData);

export const deleteUser = (userId) =>
  api.delete(`/admin/users/${userId}`);

// Director routes (Director only - manages users in their lab)
export const getLabUsers = () =>
  api.get('/users');

export const createLabUser = (userData) =>
  api.post('/users', userData);

export const resetLabUserPassword = (userId, newPassword) =>
  api.post(`/users/${userId}/reset-password`, { newPassword });

export const updateLabUser = (userId, userData) =>
  api.put(`/users/${userId}`, userData);

export const deleteLabUser = (userId) =>
  api.delete(`/users/${userId}`);

// Laboratories (Admin only)
export const getLaboratories = () =>
  api.get('/laboratories');

export const getLaboratory = (id) =>
  api.get(`/laboratories/${id}`);

export const createLaboratory = (data) =>
  api.post('/laboratories', data);

export const updateLaboratory = (id, data) =>
  api.put(`/laboratories/${id}`, data);

export const deleteLaboratory = (id) =>
  api.delete(`/laboratories/${id}`);

// Helper function for director to get their lab users
export const getDirectorUsers = () =>
  api.get('/users');

const apiExports = {
  // Auth
  login,
  register,
  getCurrentUser,
  getPublicLaboratories,
  
  // Publications
  getPublications,
  getPublication,
  createPublication,
  updatePublication,
  deletePublication,
  
  // Scholarships
  getScholarships,
  getScholarship,
  createScholarship,
  updateScholarship,
  deleteScholarship,
  
  // Congresses
  getCongresses,
  getCongress,
  createCongress,
  updateCongress,
  deleteCongress,
  
  // Meetings
  getMeetings,
  getMeeting,
  createMeeting,
  updateMeeting,
  deleteMeeting,
  updateTaskStatus,
  updateTask,
  deleteTask,
  markTaskViewed,
  addTaskMember,
  removeTaskMember,
  getDeletedTasks,
  getDeletedTask,
  markDeletedTaskViewed,
  
  // Task Messages
  getTaskMessages,
  postTaskMessage,
  getAllMessages,
  updateMessage,
  deleteMessage,
  
  // Admin routes
  getUsers,
  createUser,
  resetUserPassword,
  updateUser,
  deleteUser,
  
  // Director routes
  getLabUsers,
  getDirectorUsers,
  createLabUser,
  resetLabUserPassword,
  updateLabUser,
  deleteLabUser,
  
  // Laboratories
  getLaboratories,
  getLaboratory,
  createLaboratory,
  updateLaboratory,
  deleteLaboratory
};

export default apiExports;
